"use client";

import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Menu, Phone, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetClose } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
function BrandMark({
  size = 44,
  className
}: {
  size?: number;
  className?: string;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" width={size} height={size} className={className} aria-label="Decatur Heritage Renovations logo">
      <defs>
        <linearGradient id="za" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#eaf4ff" stopOpacity=".95" />
          <stop offset="1" stopColor="#7fa6c9" stopOpacity=".55" />
        </linearGradient>
        <linearGradient id="zb" x1="1" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#c7d9ea" stopOpacity=".9" />
          <stop offset="1" stopColor="#2a4766" stopOpacity=".7" />
        </linearGradient>
        <linearGradient id="zc" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity=".98" />
          <stop offset="1" stopColor="#b9dcf4" stopOpacity=".6" />
        </linearGradient>
        <linearGradient id="zcu" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f3c08a" />
          <stop offset=".55" stopColor="#c8793a" />
          <stop offset="1" stopColor="#8a4a1f" />
        </linearGradient>
      </defs>
      <g>
        <polygon points="20,60 60,83 60,106 20,83" fill="url(#zb)" stroke="#2b3340" strokeWidth="1.6" strokeLinejoin="round" />
        <polygon points="60,83 100,60 100,83 60,106" fill="url(#za)" stroke="#2b3340" strokeWidth="1.6" strokeLinejoin="round" />
        <polygon points="20,60 40,44 80,21 60,37" fill="url(#zb)" stroke="#2b3340" strokeWidth="1.6" strokeLinejoin="round" opacity=".85" />
        <polygon points="20,60 60,83 40,44" fill="url(#zb)" stroke="#2b3340" strokeWidth="1.6" strokeLinejoin="round" />
        <polygon points="60,83 100,60 80,21 40,44" fill="url(#zc)" stroke="#2b3340" strokeWidth="1.6" strokeLinejoin="round" />
        <line x1="40" y1="44" x2="80" y2="21" stroke="url(#zcu)" strokeWidth="3.4" strokeLinecap="round" />
        <polygon points="64,92 96,73 96,70 64,89" fill="url(#zcu)" />
        <line x1="68" y1="89" x2="68" y2="101" stroke="#2b3340" strokeWidth="2.2" />
        <line x1="80" y1="82" x2="80" y2="94" stroke="#2b3340" strokeWidth="2.2" />
        <line x1="92" y1="75" x2="92" y2="87" stroke="#2b3340" strokeWidth="2.2" />
        <polygon points="72,89 77,86 77,96 72,99" fill="url(#zcu)" />
        <polygon points="83,80 89,76.5 89,83 83,86.5" fill="#fff" stroke="#c8793a" strokeWidth=".9" />
        <polygon points="28,72 36,76.5 36,84 28,79.5" fill="#fff" opacity=".6" stroke="#c8793a" strokeWidth=".9" />
        <polygon points="42,80 50,84.5 50,92 42,87.5" fill="#fff" opacity=".6" stroke="#c8793a" strokeWidth=".9" />
        <polygon points="54,48 64,42 64,50 54,56" fill="#fff" opacity=".7" stroke="#c8793a" strokeWidth=".9" />
        <path d="M64 72 L84 38" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" opacity=".85" />
        <circle cx="80" cy="21" r="2.8" fill="#f3c08a" />
      </g>
    </svg>;
}
function DesktopSubmenuItems({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  return <ul className={`grid gap-1.5 p-1 ${depth > 0 ? "pl-3 border-l border-border" : "min-w-[200px]"}`}>
      {items.map((item, i) => {
      const hasChildren = item.children && item.children.length > 0;
      return <li key={item.id || `sub-${i}`} data-index={i} className="flex flex-col">
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-accent hover:text-accent-foreground transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
            {hasChildren && item.children && <DesktopSubmenuItems items={item.children} depth={depth + 1} />}
          </li>;
    })}
    </ul>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-2">
        <div className="h-8 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-24 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => {
        const hasChildren = item.children && item.children.length > 0;
        return <NavigationMenuItem key={item.id || `nav-${i}`} data-index={i}>
              {hasChildren && item.children ? <>
                  <NavigationMenuTrigger className="bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent/70 font-medium text-sm px-2.5 lg:px-3 py-2 h-9">
                    {item.label}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="border border-border bg-popover text-popover-foreground shadow-md rounded-md p-2">
                    <DesktopSubmenuItems items={item.children} />
                  </NavigationMenuContent>
                </> : <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="inline-flex h-9 items-center justify-center rounded-md px-2.5 lg:px-3 py-2 text-sm font-medium text-foreground hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground transition-colors">
                    {item.label}
                  </Link>
                </NavigationMenuLink>}
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileSubmenuItems({
  items,
  depth = 0,
  onItemClick
}: {
  items: MenuItem[];
  depth?: number;
  onItemClick?: () => void;
}) {
  return <ul className={`flex flex-col gap-1 ${depth > 0 ? "pl-4 border-l border-border mt-1" : ""}`}>
      {items.map((item, i) => {
      const hasChildren = item.children && item.children.length > 0;
      return <li key={item.id || `mobile-sub-${i}`} data-index={i} className="flex flex-col">
            <Link to={item.href || "#"} onClick={onItemClick} className="rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-accent hover:text-accent-foreground transition-colors">
              {item.label}
            </Link>
            {hasChildren && item.children && <MobileSubmenuItems items={item.children} depth={depth + 1} onItemClick={onItemClick} />}
          </li>;
    })}
    </ul>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});
  const toggleDropdown = (id: string) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  if (loading) {
    return <div className="flex flex-col gap-2 p-2">
        <div className="h-9 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-full bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-1 p-2" aria-label="Mobile navigation">
      {menuItems.map((item, i) => {
      const hasChildren = item.children && item.children.length > 0;
      const isOpen = openDropdowns[item.id] ?? true;
      return <div key={item.id || `mobile-nav-${i}`} data-index={i} className="flex flex-col">
            {hasChildren && item.children ? <div>
                <Button type="button" variant="ghost" onClick={() => toggleDropdown(item.id)} className="flex w-full items-center justify-between rounded-md px-3 py-2 text-base font-medium text-foreground hover:bg-accent hover:text-accent-foreground transition-colors text-left h-auto">
                  <span>{item.label}</span>
                  <ChevronDown className={`size-4 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </Button>
                {isOpen && <div className="pt-1 pb-2">
                    <MobileSubmenuItems items={item.children} onItemClick={onNavigate} />
                  </div>}
              </div> : <Link to={item.href || "#"} onClick={onNavigate} className="rounded-md px-3 py-2 text-base font-medium text-foreground hover:bg-accent hover:text-accent-foreground transition-colors">
                {item.label}
              </Link>}
          </div>;
    })}
    </nav>;
}
export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
      <section id="header" data-nav="light" className="sticky top-0 z-40 w-full bg-background/95 backdrop-blur-md border-b border-border">
        <motion.div initial={prefersReducedMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: -16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8 gap-4">
          {/* Left: Brand Logo Lockup */}
          <div className="flex items-center shrink-0">
            <Link to="/" className="flex items-center gap-3 outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md group">
              <BrandMark size={44} />
              <div className="flex flex-col text-left">
                <span className="font-serif font-semibold text-lg sm:text-xl tracking-tight leading-tight text-foreground group-hover:text-primary transition-colors">
                  Decatur Heritage
                </span>
                <span className="text-[10px] font-sans font-bold tracking-[0.2em] text-primary uppercase leading-none">
                  RENOVATIONS
                </span>
              </div>
            </Link>
          </div>

          {/* Center: Desktop Navigation Menu */}
          <div className="hidden lg:flex items-center justify-center shrink">
            <MenuProvider menu_id="46">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {/* Right: Phone & CTA Button */}
          <div className="hidden sm:flex items-center gap-3 lg:gap-4 shrink-0">
            <a href="tel:+14708479076" className="flex items-center gap-2 rounded-md px-2.5 lg:px-3 py-2 text-sm font-medium text-foreground hover:text-accent-foreground hover:bg-accent/50 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring whitespace-nowrap">
              <div className="flex size-7 items-center justify-center rounded-sm bg-secondary text-primary shrink-0">
                <Phone className="size-3.5" />
              </div>
              <span className="font-semibold text-xs lg:text-sm">{"(470) 847-9076"}</span>
            </a>

            <Button asChild size="default" className="shadow-xs font-semibold whitespace-nowrap px-3.5 lg:px-4">
              <Link to="#">Request an Estimate</Link>
            </Button>
          </div>

          {/* Mobile View: Hamburger Button & Quick Call */}
          <div className="flex items-center gap-2 sm:gap-3 lg:hidden">
            <a href="tel:+14708479076" className="flex size-9 items-center justify-center rounded-md bg-secondary text-primary hover:bg-accent transition-colors sm:hidden" aria-label="Call (470) 847-9076">
              <Phone className="size-4" />
            </a>

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="size-9 bg-background border-border text-foreground" aria-label="Open main menu">
                  <Menu className="size-5" />
                </Button>
              </SheetTrigger>

              <SheetContent side="right" className="w-full max-w-xs sm:max-w-sm flex flex-col justify-between p-6 bg-background">
                <div>
                  <SheetHeader className="p-0 pb-4 text-left border-b border-border">
                    <SheetTitle className="flex items-center">
                      <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2.5 outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md">
                        <BrandMark size={40} />
                        <div className="flex flex-col text-left">
                          <span className="font-serif font-semibold text-base tracking-tight leading-tight text-foreground">
                            Decatur Heritage
                          </span>
                          <span className="text-[9px] font-sans font-bold tracking-[0.18em] text-primary uppercase leading-none">
                            RENOVATIONS
                          </span>
                        </div>
                      </Link>
                    </SheetTitle>
                    <SheetDescription className="sr-only">
                      Navigation menu and quick contact links
                    </SheetDescription>
                  </SheetHeader>

                  <div className="py-4 overflow-y-auto max-h-[calc(100vh-280px)]">
                    <MenuProvider menu_id="46">
                      <MobileMenuConsumer onNavigate={() => setMobileMenuOpen(false)} />
                    </MenuProvider>
                  </div>
                </div>

                <div className="flex flex-col gap-3 pt-4 border-t border-border mt-auto">
                  <a href="tel:+14708479076" className="flex items-center gap-3 rounded-md p-2 text-sm font-medium text-foreground hover:bg-accent transition-colors">
                    <div className="flex size-8 items-center justify-center rounded-md bg-secondary text-primary">
                      <Phone className="size-4" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Call our team</p>
                      <p className="font-semibold text-foreground">{"(470) 847-9076"}</p>
                    </div>
                  </a>

                  <SheetClose asChild>
                    <Button asChild className="w-full justify-center font-semibold">
                      <Link to="#" onClick={() => setMobileMenuOpen(false)}>
                        Request an Estimate
                      </Link>
                    </Button>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </motion.div>
      </section>

      {/* Sticky Bottom Bar for Mobile & Small Screens */}
      <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-background/90 backdrop-blur-md border-t border-border p-3 shadow-lg flex items-center gap-3">
        <Button asChild variant="outline" className="flex-1 bg-card/90 border-border text-foreground font-semibold gap-2 h-11">
          <a href="tel:+14708479076">
            <Phone className="size-4 text-primary shrink-0" />
            <span>Call</span>
          </a>
        </Button>
        <Button asChild className="flex-1 font-semibold h-11 shadow-sm">
          <Link to="#">Request an Estimate</Link>
        </Button>
      </div>
    </>;
}